import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { User, Mail, Phone, Calendar, MapPin, ShieldCheck, Check, Sparkles, Gift, Loader2, CheckCircle, Zap, Cpu } from 'lucide-react';
import { State, City } from 'country-state-city';
const kgvHero = '/kgv4.png';

const useCashfreeSDK = () => {
  const [sdkLoaded, setSdkLoaded] = useState(false);

  useEffect(() => {
    const existingScript = document.querySelector('script[src*="cashfree"]');

    if (existingScript && window.Cashfree) {
      setSdkLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    script.async = true;

    script.onload = () => {
      if (window.Cashfree) {
        setSdkLoaded(true);
      }
    };

    script.onerror = () => console.error('❌ Failed to load Cashfree SDK');
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, []);

  const openCheckout = (sessionId) => {
    if (!window.Cashfree || !sdkLoaded) return false;

    try {
      console.log('🚀 Initializing Cashfree in production mode');
      const cf = window.Cashfree({ mode: 'production' });
      cf.checkout({
        paymentSessionId: sessionId,
        redirectTarget: '_self',
      });
      return true;
    } catch (error) {
      console.error('❌ Cashfree checkout initialization error:', error);
      return false;
    }
  };

  return { sdkLoaded, openCheckout };
};

export const Booking = () => {
  const formRef = useRef(null);
  const [engine, setEngine] = useState('125cc');
  const [batteryType] = useState('Lithium Ion');
  const [batteryPortability] = useState('Portable');
  const [addon] = useState('IoT Integration');
  const [color, setColor] = useState('Matte Black');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStateCode, setSelectedStateCode] = useState('');
  const [bookingsCount, setBookingsCount] = useState(1); // Default base seed offset (starts at 1 booking, 999 slots remaining)
  const [paymentStatus, setPaymentStatus] = useState('idle'); // 'idle', 'processing', 'success', 'error'
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const { sdkLoaded, openCheckout } = useCashfreeSDK();

  useEffect(() => {
    const fetchCount = async () => {
      try {
        // Try the main production API URL first
        const response = await axios.get('https://auth.kgvl.co.in/api/bike-bookings/count');
        if (response.data && response.data.success) {
          setBookingsCount(response.data.count);
          return;
        }
      } catch (err) {
        console.warn("Failed to fetch booking count from production API, trying local/proxy API...", err);
      }

      try {
        // Try relative api route
        const response = await axios.get('/api/bike-bookings/count');
        if (response.data && response.data.success) {
          setBookingsCount(response.data.count);
        }
      } catch (e) {
        console.warn("Failed to fetch booking count from local/proxy API. Count remains at default seed 0", e);
      }
    };
    fetchCount();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('order_id');
    const status = params.get('status');

    if (orderId && status === 'success') {
      const storedData = localStorage.getItem('pendingBookingOrderData');
      if (storedData) {
        const orderData = JSON.parse(storedData);
        if (orderData.orderId === orderId) {
          verifyPayment(orderId, orderData);
        }
      }
    }
  }, []);

  const verifyPayment = async (orderId, storedData) => {
    try {
      setPaymentStatus('processing');
      setLoading(true);
      
      // Try production verify URL first, fallback to relative URL
      let verifyResponse;
      try {
        verifyResponse = await axios.post(
          'https://auth.kgvl.co.in/api/bike-bookings/verify',
          {
            orderId: orderId,
            name: storedData.name,
            phone: storedData.phone,
            email: storedData.email,
            idProof: storedData.idProof,
            idNumber: storedData.idNumber,
            vehicleNo: storedData.vehicleNo,
            model: storedData.model,
            bikeCC: storedData.bikeCC,
            chassisNo: storedData.chassisNo,
            chargerType: storedData.chargerType,
            batteryType: storedData.batteryType,
            batteryCapacity: storedData.batteryCapacity,
            country: storedData.country,
            state: storedData.state,
            city: storedData.city,
            address1: storedData.address1,
            address2: storedData.address2,
            pinCode: storedData.pinCode,
            landmark: storedData.landmark,
            color: storedData.color,
            bookingAmount: storedData.bookingAmount,
            totalCost: storedData.totalCost
          },
          {
            timeout: 20000 // 20s timeout for verification attempts
          }
        );
      } catch (err) {
        console.warn("Verify payment failed via production API, falling back to relative API...", err);
        verifyResponse = await axios.post(
          '/api/bike-bookings/verify',
          {
            orderId: orderId,
            name: storedData.name,
            phone: storedData.phone,
            email: storedData.email,
            idProof: storedData.idProof,
            idNumber: storedData.idNumber,
            vehicleNo: storedData.vehicleNo,
            model: storedData.model,
            bikeCC: storedData.bikeCC,
            chassisNo: storedData.chassisNo,
            chargerType: storedData.chargerType,
            batteryType: storedData.batteryType,
            batteryCapacity: storedData.batteryCapacity,
            country: storedData.country,
            state: storedData.state,
            city: storedData.city,
            address1: storedData.address1,
            address2: storedData.address2,
            pinCode: storedData.pinCode,
            landmark: storedData.landmark,
            color: storedData.color,
            bookingAmount: storedData.bookingAmount,
            totalCost: storedData.totalCost
          },
          {
            timeout: 20000
          }
        );
      }

      if (verifyResponse.data.success) {
        const order = verifyResponse.data.order;
        setOrderDetails({
          orderId: orderId,
          paymentId: order?.cashfree_payment_id || verifyResponse.data.paymentStatus || 'SUCCESS',
          amount: storedData.bookingAmount,
          customerName: storedData.name,
          customerEmail: storedData.email,
          customerPhone: storedData.phone,
          model: storedData.model,
          timestamp: new Date().toLocaleString(),
          paymentStatus: verifyResponse.data.paymentStatus || 'SUCCESS',
        });
        setPaymentStatus('success');
        if (window.fbq) {
          window.fbq('track', 'Purchase', {
            content_name: 'KGV Hybrid Bike Booking',
            content_category: 'Vehicle Booking',
            value: storedData.bookingAmount,
            currency: 'INR',
          });
        }
        setShowSuccessModal(true);
        localStorage.removeItem('pendingBookingOrderData');
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      } else {
        setPaymentStatus('error');
        setError(verifyResponse.data.message || 'Payment verification failed');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setPaymentStatus('error');
      setError(err.response?.data?.message || err.message || 'Payment verification failed');
    } finally {
      setLoading(false);
    }
  };

  const statesList = State.getStatesOfCountry('IN');
  const citiesList = selectedStateCode ? City.getCitiesOfState('IN', selectedStateCode) : [];
  
  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    fatherName: '',
    dob: '',
    phone: '',
    email: '',
    idProof: '',
    idNumber: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    pinCode: '',
    landmark: ''
  });

  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');

  const sendBookingOtp = async () => {
    setOtpLoading(true);
    setOtpError('');
    try {
      const cleanPhone = formData.phone.replace(/\s+/g, "");
      const res = await axios.post('https://auth.kgvl.co.in/api/otp/send', { phone: cleanPhone });
      if (res.data.success) {
        setOtpSent(true);
      } else {
        setOtpError(res.data.message || 'Failed to send OTP');
      }
    } catch (err) {
      setOtpError(err.response?.data?.message || err.message || 'Error sending OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyBookingOtp = async () => {
    setOtpLoading(true);
    setOtpError('');
    try {
      const cleanPhone = formData.phone.replace(/\s+/g, "");
      const res = await axios.post('https://auth.kgvl.co.in/api/otp/verify', { phone: cleanPhone, otp: otpCode });
      if (res.data.success) {
        setOtpVerified(true);
      } else {
        setOtpError(res.data.message || 'Invalid OTP');
      }
    } catch (err) {
      setOtpError(err.response?.data?.message || err.message || 'Invalid OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  // Auto-restore form fields if user returns from a pending checkout
  useEffect(() => {
    const stored = localStorage.getItem('pendingBookingOrderData');
    if (stored) {
      try {
        const data = JSON.parse(stored);
        setFormData(prev => ({
          ...prev,
          fullName: data.name || prev.fullName,
          phone: data.phone || prev.phone,
          email: data.email || prev.email,
          idProof: data.idProof === 'Aadhar' ? 'Aadhaar' : (data.idProof || prev.idProof),
          idNumber: data.idNumber || prev.idNumber,
          state: data.state || prev.state,
          city: data.city || prev.city,
          address1: data.address1 || prev.address1,
          address2: data.address2 || prev.address2,
          pinCode: data.pinCode || prev.pinCode,
          landmark: data.landmark || prev.landmark
        }));
        if (data.bikeCC === 160 || data.bikeCC === '160cc' || data.model?.includes('160cc')) {
          setEngine('160cc');
        } else {
          setEngine('125cc');
        }
        if (data.color && (data.color === 'Matte Black' || data.color === 'Royal Blue')) {
          setColor(data.color);
        } else {
          setColor('Matte Black');
        }
        
        if (data.state) {
          const matchedState = State.getStatesOfCountry('IN').find(
            s => s.name.toLowerCase() === data.state.toLowerCase() || 
                 s.isoCode.toLowerCase() === data.state.toLowerCase()
          );
          if (matchedState) {
            setSelectedStateCode(matchedState.isoCode);
          }
        }
      } catch (err) {
        console.warn("Failed to restore pending booking form data:", err);
      }
    }
  }, []);

  const originalCost = engine === '160cc' ? 195000 : 175000;
  const earlyBirdDiscount = 50000;
  const totalCost = originalCost - earlyBirdDiscount;
  const bookingAmount = 10000;
  const balanceDue = totalCost - bookingAmount;

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleBooking = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.fullName || !formData.phone || !formData.email || !formData.idProof || !formData.idNumber || !formData.state || !formData.city || !formData.pinCode) {
        throw new Error('Please fill in all required fields (Full Name, Phone, Email, ID Proof, ID Number, State, City, and Pin Code)');
      }

      const cleanPin = formData.pinCode.trim();
      if (!/^\d{6}$/.test(cleanPin)) {
        throw new Error('Invalid Pin Code. Must be exactly 6 digits');
      }

      const phoneNumber = formData.phone.replace(/\s+/g, '');
      if (!/^[6-9]\d{9}$/.test(phoneNumber)) {
        throw new Error('Invalid phone number. Must be 10 digits starting with 6-9');
      }

      // Validate ID Proof formats
      const idNum = formData.idNumber.trim();
      const idType = formData.idProof;
      if (idType === "Aadhaar") {
        const cleanAadhar = idNum.replace(/\s+/g, "");
        if (!/^\d{12}$/.test(cleanAadhar)) {
          throw new Error('Invalid Aadhaar Card number. Must be a 12-digit number');
        }
      } else if (idType === "PAN Card") {
        const cleanPan = idNum.toUpperCase().replace(/\s+/g, "");
        if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(cleanPan)) {
          throw new Error('Invalid PAN Card format. Must match standard format (e.g., ABCDE1234F)');
        }
      } else if (idType === "Driving License") {
        if (idNum.length < 8 || idNum.length > 20) {
          throw new Error('Invalid Driving License format. Please enter a valid DL number');
        }
      }

      // Create order on backend - try production first, fallback to relative
      let response;
      try {
        response = await axios.post('https://auth.kgvl.co.in/api/bike-bookings/checkout', {
          amount: bookingAmount,
          customerName: formData.fullName,
          customerEmail: formData.email,
          customerPhone: phoneNumber,
        }, {
          timeout: 10000 // 10 seconds timeout to prevent hanging if backend is slow/offline
        });
      } catch (err) {
        console.warn("Checkout failed via production API, falling back to relative API...", err);
        response = await axios.post('/api/bike-bookings/checkout', {
          amount: bookingAmount,
          customerName: formData.fullName,
          customerEmail: formData.email,
          customerPhone: phoneNumber,
        }, {
          timeout: 10000
        });
      }

      if (response.data.success && response.data.sessionId) {
        // Map values for the Order schema requirements
        const mappedOrderData = {
          orderId: response.data.orderId,
          name: formData.fullName,
          phone: phoneNumber,
          email: formData.email,
          idProof: formData.idProof,
          idNumber: formData.idNumber,
          vehicleNo: "NEW_BIKE",
          model: `KGV E-Hybrid (${engine}, ${batteryType}, ${batteryPortability}, ${addon}) - Color: ${color}`,
          bikeCC: parseInt(engine),
          chassisNo: "NEW_BIKE",
          chargerType: addon === "Charger variation" ? "15AMP" : "5AMP",
          batteryType: batteryType,
          batteryCapacity: batteryType === "Sodium Icon" ? "72v 12A/H" : "72v 15A/H",
          country: "India",
          state: formData.state,
          city: formData.city,
          address1: formData.address1,
          address2: formData.address2,
          pinCode: formData.pinCode,
          landmark: formData.landmark,
          color: color,
          bookingAmount: bookingAmount,
          totalCost: totalCost,
          timestamp: Date.now(),
        };

        // Store in local storage
        localStorage.setItem('pendingBookingOrderData', JSON.stringify(mappedOrderData));

        // Trigger checkout
        console.log("💰 Checkout API Response:", response.data);
        openCheckout(response.data.sessionId);
      } else {
        throw new Error(response.data.message || 'Failed to create payment session');
      }
    } catch (err) {
      console.error('Booking error:', err);
      let errorMsg = 'Failed to process booking. Please try again.';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMsg = 'Connection timed out. The server is taking too long to respond. Please check your internet connection or try again later.';
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
      // Smoothly scroll to the top of the customer details form on error
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    } finally {
      setLoading(false);
    }
  };

  if (showSuccessModal && orderDetails) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 py-12 px-4 sm:px-6 lg:px-8 font-outfit relative overflow-hidden flex items-center justify-center">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl -z-10"></div>
        <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -z-10"></div>

        <div className="max-w-4xl w-full">
          <div className="bg-slate-900/80 border border-emerald-500/30 rounded-2xl p-8 mb-6 text-center backdrop-blur-xl shadow-[0_0_50px_rgba(16,185,129,0.15)]">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-6">
              <CheckCircle className="w-12 h-12 text-emerald-400" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold bg-gradient-to-r from-white via-slate-100 to-emerald-400 bg-clip-text text-transparent mb-2">
              Reservation Confirmed!
            </h1>
            <p className="text-lg text-slate-400">
              Your KGV Hybrid E-Bike slot has been successfully secured.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 backdrop-blur-xl">
              <h2 className="text-xl font-bold text-slate-200 mb-4">
                Booking Summary
              </h2>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Booking ID</p>
                  <p className="font-semibold text-slate-300">{orderDetails.orderId}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Advance Paid</p>
                  <p className="text-2xl font-black text-emerald-400 mt-1">
                    ₹{orderDetails.amount?.toLocaleString('en-IN')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Payment Status</p>
                  <span className="inline-block mt-1 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-full text-xs font-bold">
                    {orderDetails.paymentStatus}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 backdrop-blur-xl">
              <h2 className="text-xl font-bold text-slate-200 mb-4">
                Reservation & Customer Details
              </h2>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Customer Name</p>
                  <p className="font-semibold text-slate-300">{orderDetails.customerName}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Build Configuration</p>
                  <p className="font-semibold text-slate-300">{orderDetails.model}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Contact Info</p>
                  <p className="text-slate-300">{orderDetails.customerPhone} | {orderDetails.customerEmail}</p>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={() => window.location.reload()}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold py-4 px-6 rounded-xl transition duration-300 shadow-[0_4px_25px_rgba(16,185,129,0.2)]"
          >
            Configure Another Bike
          </button>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'processing') {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-4">
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-12 max-w-md w-full text-center shadow-2xl">
          <Loader2 className="w-16 h-16 text-emerald-400 animate-spin mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-slate-100 mb-2">Verifying Payment</h2>
          <p className="text-slate-400 text-sm">
            Please wait while we confirm your reservation with Cashfree...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 py-12 px-4 sm:px-6 lg:px-8 font-outfit relative overflow-hidden">
      {/* Import Outfit Font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
        .font-outfit {
          font-family: 'Outfit', sans-serif;
        }
      `}</style>

      {/* Decorative Radial Gradients */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -z-10"></div>

      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <span className="text-xs font-bold tracking-[0.3em] text-emerald-400 uppercase">Secure Your Ride</span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mt-3 bg-gradient-to-r from-white via-slate-100 to-emerald-400 bg-clip-text text-transparent leading-tight">
            Unleash the Future with KGV Hybrid
          </h1>
          <p className="text-slate-400 text-sm sm:text-base max-w-2xl mx-auto mt-4 leading-relaxed">
            Experience seamless electric efficiency, robust build quality, and zero range anxiety. Build your perfect configuration and secure it with a ₹10,000 early bird reservation.
          </p>
        </div>

        {/* Mobile-only Bike Preview Card */}
        <div className="block lg:hidden mb-8 bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
          <div className="relative h-64 bg-slate-950 flex items-center justify-center p-4 overflow-hidden">
            <div className="absolute w-48 h-48 rounded-full bg-emerald-500/10 blur-3xl top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></div>
            <img 
              src={kgvHero} 
              alt="KGV Hybrid E-Bike Preview" 
              className="max-h-full object-contain relative z-10 drop-shadow-[0_10px_25px_rgba(16,185,129,0.25)] scale-110" 
            />
            <span className="absolute bottom-3 left-3 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5 rounded-full text-[9px] font-bold text-emerald-400 tracking-wider flex items-center gap-1">
              <Sparkles className="w-2.5 h-2.5 animate-spin" /> LIVE BUILD
            </span>
          </div>
          <div className="p-5 bg-slate-900/80 flex justify-between items-center border-t border-slate-800/80">
            <div>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Estimated Value</p>
              <div className="flex flex-wrap items-baseline gap-1.5 mt-0.5">
                <span className="text-xl font-black text-emerald-400">₹{totalCost.toLocaleString('en-IN')}</span>
                <span className="text-xs text-slate-500 line-through">₹{originalCost.toLocaleString('en-IN')}</span>
                <span className="text-[9px] text-emerald-500 font-bold bg-emerald-500/10 px-1 py-0.5 rounded">-₹50k</span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Booking Amount</p>
              <p className="text-sm font-extrabold text-slate-200 mt-0.5">₹{bookingAmount.toLocaleString('en-IN')}</p>
            </div>
          </div>
        </div>

        {/* Main Interface: Sidebar + Configurator */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Left Column: Interactive Configuration Builder (Lg: 7 cols) */}
          <div className="lg:col-span-7 space-y-8">
            
            {/* Early Bird Campaign Countdown Banner */}
            <div className="bg-slate-900/80 border border-emerald-500/30 rounded-2xl p-6 backdrop-blur-xl shadow-[0_0_30px_rgba(16,185,129,0.1)] relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl"></div>
              
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 relative z-10">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping"></span>
                    <span className="text-xs font-bold text-emerald-400 uppercase tracking-widest">Early Bird Discount Offer</span>
                  </div>
                  <h2 className="text-xl font-bold text-slate-100 font-outfit">
                    First 1000 Bookings Special
                  </h2>
                  <p className="text-xs text-slate-400 mt-1">
                    Secure ₹50,000 off and get <strong className="text-slate-200">₹12,000 in Early Bird Gifts</strong> (₹8,000 KGV Smart Helmet, ₹2,500 Goodies & ₹1,500 KGV Club Membership)!
                  </p>
                </div>
                
                <div className="text-left sm:text-right shrink-0">
                  <div className="text-sm font-bold text-slate-300">
                    <span className="text-2xl text-emerald-400 font-extrabold font-outfit">{Math.max(0, 1000 - bookingsCount)}</span> / 1000 slots left
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5">Campaign ends when slots reach 0</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-4 relative h-2.5 w-full bg-slate-950 rounded-full overflow-hidden border border-slate-800">
                <div 
                  className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(16,185,129,0.5)]"
                  style={{ width: `${Math.max(0, Math.min(100, ((1000 - bookingsCount) / 1000) * 100))}%` }}
                ></div>
              </div>
            </div>

            {/* Step 1: Configuration Options */}
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 sm:p-8 backdrop-blur-xl shadow-xl space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">1</div>
                <h2 className="text-xl font-bold text-slate-100">Choose Engine & Color</h2>
              </div>
              
              {/* Engine Selection */}
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Choose Engine Capacity*</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { cc: '125cc', price: '₹1,75,000', finalCost: '₹1,25,000' },
                    { cc: '160cc', price: '₹1,95,000', finalCost: '₹1,45,000' }
                  ].map((item) => (
                    <button
                      key={item.cc}
                      type="button"
                      onClick={() => setEngine(item.cc)}
                      className={`py-3.5 px-4 rounded-xl border text-left transition duration-300 relative flex flex-col justify-between min-h-[95px] ${
                        engine === item.cc 
                          ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.15)]' 
                          : 'border-slate-800 bg-slate-950/50 hover:bg-slate-900 text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      {engine === item.cc && <Check className="w-4 h-4 absolute top-3 right-3 text-emerald-400" />}
                      <div>
                        <span className="text-base font-extrabold block font-outfit">{item.cc} Engine</span>
                        <span className="text-[10px] text-slate-400 mt-0.5 block">Original Price: <span className="line-through">{item.price}</span></span>
                      </div>
                      <span className="text-[10px] sm:text-xs font-bold text-emerald-400 mt-2 block">{item.finalCost} ex-showroom (after ₹50k discount)</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Color Selection */}
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Choose Your Aesthetic (Color)*</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { name: 'Matte Black', colorClass: 'bg-zinc-900 border-zinc-700' },
                    { name: 'Royal Blue', colorClass: 'bg-blue-900 border-blue-700' }
                  ].map((item) => (
                    <button
                      key={item.name}
                      type="button"
                      onClick={() => setColor(item.name)}
                      className={`py-3 px-4 rounded-xl border text-xs sm:text-sm font-bold transition duration-300 flex flex-col items-center justify-center gap-2 relative ${
                        color === item.name 
                          ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.15)]' 
                          : 'border-slate-800 bg-slate-950/50 hover:bg-slate-900 text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      {color === item.name && <Check className="w-3.5 h-3.5 absolute top-2 right-2 text-emerald-400" />}
                      <span className={`w-4 h-4 rounded-full ${item.colorClass} border`}></span>
                      <span>{item.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-slate-800/80 pt-6"></div>

              {/* Specifications Showcase */}
              <div>
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Standard Specifications Included</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Engine Card */}
                  <div className="bg-slate-950/60 border border-slate-800/80 p-5 rounded-2xl flex items-start gap-4 transition-all duration-300 hover:border-emerald-500/30 group">
                    <div className="p-3 bg-emerald-500/10 rounded-xl text-emerald-400 shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <Zap className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-200 text-base">{engine} Hybrid Power</h3>
                      <p className="text-slate-400 text-xs mt-1 leading-relaxed">
                        Optimally tuned {engine} petrol-electric hybrid motor with high torque and seamless power transitions.
                      </p>
                    </div>
                  </div>

                  {/* Battery Chemistry Card */}
                  <div className="bg-slate-950/60 border border-slate-800/80 p-5 rounded-2xl flex items-start gap-4 transition-all duration-300 hover:border-emerald-500/30 group">
                    <div className="p-3 bg-emerald-500/10 rounded-xl text-emerald-400 shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <CheckCircle className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-200 text-base">Lithium-Ion Battery</h3>
                      <p className="text-slate-400 text-xs mt-1 leading-relaxed">
                        Premium high-density cells (72v 15A/H capacity) designed for rapid charging and maximum battery life.
                      </p>
                    </div>
                  </div>

                  {/* Battery System Portability Card */}
                  <div className="bg-slate-950/60 border border-slate-800/80 p-5 rounded-2xl flex items-start gap-4 transition-all duration-300 hover:border-emerald-500/30 group">
                    <div className="p-3 bg-emerald-500/10 rounded-xl text-emerald-400 shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <ShieldCheck className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-200 text-base">Portable Battery System</h3>
                      <p className="text-slate-400 text-xs mt-1 leading-relaxed">
                        Conveniently detachable battery. Carry it easily and charge at any standard household or office wall socket.
                      </p>
                    </div>
                  </div>

                  {/* IoT Integration Card */}
                  <div className="bg-slate-950/60 border border-slate-800/80 p-5 rounded-2xl flex items-start gap-4 transition-all duration-300 hover:border-emerald-500/30 group">
                    <div className="p-3 bg-emerald-500/10 rounded-xl text-emerald-400 shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <Cpu className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-200 text-base">IoT Integration</h3>
                      <p className="text-slate-400 text-xs mt-1 leading-relaxed">
                        Smart App Access with live GPS vehicle tracking, geo-fencing, anti-theft alarms, and health diagnostic alerts.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2: Personal & Billing details */}
            <div ref={formRef} className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 sm:p-8 backdrop-blur-xl shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">2</div>
                <h2 className="text-xl font-bold text-slate-100">Customer & Delivery Information</h2>
              </div>

              <form className="space-y-5" onSubmit={handleBooking}>
                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-xl text-sm">
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Full Name */}
                  <div className="relative">
                    <User className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
                    <input 
                      className="w-full pl-10 pr-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition duration-200 text-sm disabled:opacity-75 disabled:cursor-not-allowed" 
                      placeholder="Full Name*" 
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      disabled={otpVerified}
                    />
                  </div>

                  {/* Phone */}
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
                    <input 
                      className="w-full pl-10 pr-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition duration-200 text-sm disabled:opacity-75 disabled:cursor-not-allowed" 
                      placeholder="Phone Number*" 
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      disabled={otpVerified}
                    />
                  </div>

                  {/* OTP Verification UI */}
                  {!otpVerified && (
                    <div className="col-span-1 sm:col-span-2 p-4 bg-slate-950/60 border border-slate-800 rounded-xl space-y-3">
                      {!otpSent ? (
                        <button
                          type="button"
                          onClick={sendBookingOtp}
                          disabled={otpLoading || !/^[6-9]\d{9}$/.test(formData.phone.replace(/\s+/g, ""))}
                          className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-800 disabled:text-slate-500 text-slate-950 font-bold py-2 px-4 rounded-lg transition duration-200 text-sm flex items-center justify-center gap-2"
                        >
                          {otpLoading ? <Loader2 className="w-4 h-4 animate-spin text-slate-950" /> : "Send OTP"}
                        </button>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-xs text-slate-400">An OTP has been sent to {formData.phone}. Please enter it below:</p>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              maxLength={6}
                              placeholder="Enter 6-Digit OTP"
                              value={otpCode}
                              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                              className="flex-1 px-4 py-2 bg-slate-950 border border-slate-800 rounded-lg text-slate-100 placeholder-slate-600 text-sm focus:outline-none focus:border-emerald-500"
                            />
                            <button
                              type="button"
                              onClick={verifyBookingOtp}
                              disabled={otpLoading || otpCode.length !== 6}
                              className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-800 disabled:text-slate-500 text-slate-950 font-bold px-4 py-2 rounded-lg transition duration-200 text-sm flex items-center justify-center"
                            >
                              {otpLoading ? <Loader2 className="w-4 h-4 animate-spin text-slate-950" /> : "Verify"}
                            </button>
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <button
                              type="button"
                              onClick={sendBookingOtp}
                              disabled={otpLoading}
                              className="text-emerald-400 hover:underline"
                            >
                              Resend OTP
                            </button>
                            {otpError && <span className="text-red-400">{otpError}</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {otpVerified && (
                    <div className="col-span-1 sm:col-span-2 text-xs text-emerald-400 font-semibold flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 px-3 py-2 rounded-lg">
                      <CheckCircle className="w-4 h-4" /> Phone Number Verified Successfully. You can now fill in the remaining details.
                    </div>
                  )}

                  {/* Email */}
                  <div className="relative sm:col-span-2">
                    <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
                    <input 
                      className="w-full pl-10 pr-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed" 
                      placeholder="Email Address*" 
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={!otpVerified}
                    />
                  </div>

                  {/* DOB */}
                  <div className="relative">
                    <Calendar className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
                    <input 
                      className="w-full pl-10 pr-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed [color-scheme:dark]" 
                      placeholder="Date of Birth" 
                      name="dob"
                      type="date"
                      value={formData.dob}
                      onChange={handleInputChange}
                      disabled={!otpVerified}
                    />
                  </div>

                  {/* ID Proof Selection */}
                  <div className="relative">
                    <select 
                      className="w-full px-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition duration-200 text-sm appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      name="idProof"
                      value={formData.idProof}
                      onChange={handleInputChange}
                      required
                      disabled={!otpVerified}
                    >
                      <option value="">Select ID Proof*</option>
                      <option value="Aadhaar">Aadhaar</option>
                      <option value="Driving License">Driving License</option>
                      <option value="PAN Card">PAN Card</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-500">
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                    </div>
                  </div>

                  {/* ID Number */}
                  <div className="relative sm:col-span-2">
                    <input 
                      className="w-full px-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed" 
                      placeholder="ID Document Number*" 
                      name="idNumber"
                      value={formData.idNumber}
                      onChange={handleInputChange}
                      required
                      disabled={!otpVerified}
                    />
                  </div>

                  {/* Address Line 1 */}
                  <div className="relative sm:col-span-2">
                    <MapPin className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
                    <input 
                      className="w-full pl-10 pr-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed" 
                      placeholder="Address Line 1" 
                      name="address1"
                      value={formData.address1}
                      onChange={handleInputChange}
                      disabled={!otpVerified}
                    />
                  </div>

                  {/* Address Line 2 */}
                  <div className="relative sm:col-span-2">
                    <MapPin className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-500" />
                    <input 
                      className="w-full pl-10 pr-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed" 
                      placeholder="Address Line 2" 
                      name="address2"
                      value={formData.address2}
                      onChange={handleInputChange}
                      disabled={!otpVerified}
                    />
                  </div>

                  {/* State Selection */}
                  <div className="relative">
                    <select 
                      className="w-full px-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition duration-200 text-sm appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      name="state"
                      value={selectedStateCode}
                      onChange={(e) => {
                        const code = e.target.value;
                        const stateObj = statesList.find(s => s.isoCode === code);
                        setSelectedStateCode(code);
                        setFormData({
                          ...formData,
                          state: stateObj ? stateObj.name : '',
                          city: '' // Reset city on state change
                        });
                      }}
                      required
                      disabled={!otpVerified}
                    >
                      <option value="">Select State*</option>
                      {statesList.map((state) => (
                        <option key={state.isoCode} value={state.isoCode}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-500">
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                    </div>
                  </div>

                  {/* City Selection */}
                  <div className="relative">
                    <select 
                      className="w-full px-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition duration-200 text-sm appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      name="city"
                      value={formData.city}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          city: e.target.value
                        });
                      }}
                      disabled={!otpVerified || !selectedStateCode}
                      required
                    >
                      <option value="">Select City*</option>
                      {citiesList.map((city) => (
                        <option key={city.name} value={city.name}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-500">
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                    </div>
                  </div>

                  {/* Pin Code */}
                  <div className="relative">
                    <input 
                      className="w-full px-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed" 
                      placeholder="Pin Code (6 digits)*" 
                      name="pinCode"
                      maxLength={6}
                      value={formData.pinCode}
                      onChange={(e) => {
                        // Allow only digits
                        const val = e.target.value.replace(/\D/g, '');
                        setFormData({ ...formData, pinCode: val });
                      }}
                      required
                      disabled={!otpVerified}
                    />
                  </div>

                  {/* Landmark */}
                  <div className="relative">
                    <input 
                      className="w-full px-4 py-3 bg-slate-950/80 border border-slate-800 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed" 
                      placeholder="Landmark (Optional)" 
                      name="landmark"
                      value={formData.landmark}
                      onChange={handleInputChange}
                      disabled={!otpVerified}
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Right Column: Visual Summary + Payment Timeline (Lg: 5 cols) */}
          <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-8">
            
            {/* Visual Bike Summary Card */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
              {/* Image box with glowing circle behind */}
              <div className="hidden lg:flex relative h-80 bg-slate-950 items-center justify-center p-4 border-b border-slate-800/80 overflow-hidden">
                <div className="absolute w-60 h-60 rounded-full bg-emerald-500/10 blur-3xl top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></div>
                <img 
                  src={kgvHero} 
                  alt="KGV Hybrid E-Bike Preview" 
                  className="max-h-full object-contain relative z-10 drop-shadow-[0_10px_25px_rgba(16,185,129,0.25)] scale-125 hover:scale-130 transition-transform duration-500" 
                />
                
                <span className="absolute bottom-4 left-4 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full text-[10px] font-bold text-emerald-400 tracking-wider flex items-center gap-1">
                  <Sparkles className="w-3 h-3 animate-spin" /> LIVE BUILD
                </span>
              </div>

              {/* Specs and details list */}
              <div className="p-6 sm:p-8 space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-100">Configuration Summary</h3>
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="bg-slate-950 p-3 rounded-xl border border-slate-800">
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Engine Capacity</p>
                      <p className="text-sm font-semibold text-emerald-400 mt-0.5">{engine}</p>
                    </div>
                    <div className="bg-slate-950 p-3 rounded-xl border border-slate-800">
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Battery Type</p>
                      <p className="text-sm font-semibold text-emerald-400 mt-0.5">{batteryType}</p>
                    </div>
                    <div className="bg-slate-950 p-3 rounded-xl border border-slate-800">
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Portability</p>
                      <p className="text-sm font-semibold text-emerald-400 mt-0.5">{batteryPortability}</p>
                    </div>
                    <div className="bg-slate-950 p-3 rounded-xl border border-slate-800">
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Selected Add-on</p>
                      <p className="text-sm font-semibold text-emerald-400 mt-0.5 truncate">{addon}</p>
                    </div>
                    <div className="bg-slate-950 p-3 rounded-xl border border-slate-800 col-span-2 flex items-center justify-between">
                      <div>
                        <p className="text-[10px] text-slate-400 font-bold uppercase">Selected Color</p>
                        <p className="text-sm font-semibold text-emerald-400 mt-0.5">{color}</p>
                      </div>
                      <span className={`w-3.5 h-3.5 rounded-full border ${
                        color === 'Matte Black' ? 'bg-zinc-900 border-zinc-700' :
                        color === 'Royal Blue' ? 'bg-blue-900 border-blue-700' :
                        'bg-red-900 border-red-700'
                      }`}></span>
                    </div>
                  </div>

                  {/* Helmet Free Gift Banner */}
                  <div className="mt-4 bg-emerald-500/10 border border-emerald-500/20 p-3.5 rounded-xl flex items-center gap-3">
                    <Gift className="w-5 h-5 text-emerald-400 shrink-0 animate-bounce" />
                    <div className="text-left">
                      <p className="text-xs font-bold text-slate-100">₹12,000 Early Bird Gifts Bundle Added</p>
                      <p className="text-[10px] text-emerald-400/90 font-medium">Free KGV Smart Helmet (₹8,000) + Goodies (₹2,500) + KGV Club Membership (₹1,500)!</p>
                    </div>
                  </div>
                </div>

                {/* Price Breakdown */}
                <div className="border-t border-slate-800 pt-5 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Standard Vehicle Cost ({engine}):</span>
                    <span className="font-semibold text-slate-400 line-through">₹{originalCost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm text-emerald-400">
                    <span>Early Bird Discount (First 1000):</span>
                    <span className="font-semibold">-₹{earlyBirdDiscount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm text-slate-300">
                    <span>KGV Smart Helmet (Worth ₹8k):</span>
                    <span className="font-semibold text-emerald-400">FREE GIFT</span>
                  </div>
                  <div className="flex justify-between text-sm text-slate-300">
                    <span>Goodies & Club Membership (Worth ₹4k):</span>
                    <span className="font-semibold text-emerald-400">FREE GIFT</span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-slate-900 pt-2 text-slate-400">
                    <span>Total Ex-Showroom Cost:</span>
                    <span className="font-semibold text-slate-200">₹{totalCost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm text-slate-400">
                    <span>Expected Delivery Timeline:</span>
                    <span className="font-semibold text-emerald-400">18 Months</span>
                  </div>
                  <div className="flex justify-between text-sm text-slate-400">
                    <span>Final Balance ex-showroom (Month 18):</span>
                    <span className="font-semibold text-slate-200">₹{balanceDue.toLocaleString()}</span>
                  </div>
                  
                  {/* Delivery Taxes Note */}
                  <p className="text-[10.5px] text-slate-500 italic leading-relaxed mt-1">
                    * Note: Additional taxes like RTO, insurance, and other local registration charges are applicable extra at the time of delivery.
                  </p>

                  <div className="flex justify-between items-center text-lg font-bold border-t border-slate-800 pt-3 text-slate-100">
                    <span>Due Now (Advance Slot Lock):</span>
                    <span className="text-2xl text-emerald-400 font-extrabold font-outfit">₹{bookingAmount.toLocaleString()}</span>
                  </div>
                </div>

                {/* Non-Refundable Policy Notice */}
                <div className="bg-red-500/5 border border-red-500/10 p-3.5 rounded-xl flex items-start gap-2.5 text-xs text-slate-400">
                  <ShieldCheck className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                  <p className="leading-relaxed">
                    <strong className="text-red-400">Non-Refundable Slot Lock:</strong> Please note that this early bird slot reservation booking fee of ₹10,000 is non-refundable, as it is used to queue and procure materials for custom fabrication.
                  </p>
                </div>

                {/* Submit Action */}
                <button
                  onClick={handleBooking}
                  disabled={loading || !otpVerified}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-800 text-slate-950 font-bold py-4 px-6 rounded-xl transition duration-300 disabled:text-slate-500 disabled:cursor-not-allowed shadow-[0_4px_25px_rgba(16,185,129,0.25)] hover:shadow-[0_4px_30px_rgba(16,185,129,0.4)] flex items-center justify-center gap-2 group text-base"
                  type="button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-slate-950" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing Reservation...
                    </span>
                  ) : (
                    <>
                      Secure My KGV Hybrid
                      <span className="group-hover:translate-x-1 transition-transform">⚡</span>
                    </>
                  )}
                </button>

                {/* Security Trust Badge */}
                <div className="flex flex-col gap-2 mt-2">
                  <div className="flex items-center justify-center gap-2 text-xs text-slate-500 bg-slate-950/60 py-2.5 rounded-xl border border-slate-900">
                    <ShieldCheck className="w-4 h-4 text-emerald-500" />
                    <span>Secure Payments by Cashfree Gateway</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step Timeline Guide */}
            <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8 backdrop-blur-xl relative overflow-hidden shadow-2xl">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-3xl"></div>
              
              <h3 className="text-xl font-bold text-slate-100 mb-6 font-outfit flex items-center gap-2.5">
                <Sparkles className="w-5 h-5 text-emerald-400" />
                Reservation Milestone Plan
              </h3>
              
              <div className="space-y-8 relative before:absolute before:left-[17px] before:top-2 before:bottom-2 before:w-[2px] before:bg-gradient-to-b before:from-emerald-500 before:via-slate-800 before:to-slate-900">
                {/* Milestone 1 */}
                <div className="flex gap-5 relative group">
                  <div className="w-9 h-9 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center font-extrabold z-10 shrink-0 text-sm shadow-[0_0_20px_rgba(16,185,129,0.4)] transition-all duration-300 group-hover:scale-105">
                    1
                  </div>
                  <div className="text-left">
                    <h4 className="font-extrabold text-emerald-400 text-sm tracking-wide uppercase font-outfit">Step 1: Secure Booking Slot</h4>
                    <p className="text-slate-300 text-xs mt-1.5 leading-relaxed">
                      Lock in your early bird slot instantly by paying the <strong className="text-slate-100">₹{bookingAmount.toLocaleString()}</strong> reservation fee. This guarantees your slot, unlocks the premium launch discount, and reserves your complimentary <strong className="text-slate-100">₹12,000 gifts bundle</strong>.
                    </p>
                  </div>
                </div>

                {/* Milestone 2 */}
                <div className="flex gap-5 relative group">
                  <div className="w-9 h-9 rounded-full bg-slate-950 border-2 border-slate-800 text-slate-300 flex items-center justify-center font-bold z-10 shrink-0 text-sm transition-all duration-300 group-hover:border-emerald-500/50">
                    2
                  </div>
                  <div className="text-left">
                    <h4 className="font-bold text-slate-200 text-sm tracking-wide uppercase font-outfit">Step 2: Custom Fabrication (18 Months)</h4>
                    <p className="text-slate-400 text-xs mt-1.5 leading-relaxed">
                      Your hybrid bike configuration enters our custom fabrication pipeline. Our engineering team builds, calibrates, and integrates the smart IoT and hybrid powertrain to your selected specification within the <strong className="text-slate-300">18-month production timeline</strong>.
                    </p>
                  </div>
                </div>

                {/* Milestone 3 */}
                <div className="flex gap-5 relative group">
                  <div className="w-9 h-9 rounded-full bg-slate-950 border-2 border-slate-800 text-slate-300 flex items-center justify-center font-bold z-10 shrink-0 text-sm transition-all duration-300 group-hover:border-emerald-500/50">
                    3
                  </div>
                  <div className="text-left">
                    <h4 className="font-bold text-slate-200 text-sm tracking-wide uppercase font-outfit">Step 3: Showroom Handover & Balance</h4>
                    <p className="text-slate-400 text-xs mt-1.5 leading-relaxed">
                      Pay the remaining ex-showroom balance of <strong className="text-emerald-400 font-bold">₹{balanceDue.toLocaleString()}</strong> at your nearest delivery showroom during physical handover at Month 18. Applicable taxes (RTO, registration, and insurance) are collected at the handover counter.
                    </p>
                  </div>
                </div>
              </div>
            </div>

        </div>

      </div>

        {/* FAQ Section */}
        <div className="mt-16 bg-slate-900/40 border border-slate-800 rounded-3xl p-6 sm:p-10 backdrop-blur-xl max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-slate-100 text-center mb-8 font-outfit">Frequently Asked Questions</h3>
          <div className="space-y-4">
            {[
              {
                q: "Is the ₹10,000 reservation booking fee refundable?",
                a: "No, the ₹10,000 early bird reservation fee is strictly non-refundable. This amount is immediately allocated to secure raw materials and reserve your spot in our custom hybrid bike production queue."
              },
              {
                q: "When will the delivery of the E-Hybrid start?",
                a: "Fabrication and assembly are queued strictly in the order received. Deliveries will commence in batches, with a target delivery timeline of up to 18 months from the date of booking slot reservation."
              },
              {
                q: "How do I choose the color of my bike?",
                a: "You can select your preferred color (Matte Black or Royal Blue) right here in Step 1 of this booking page. It will be recorded as part of your locked order details."
              },
              {
                q: "How and when do I pay the remaining balance amount?",
                a: "The ex-showroom balance amount (₹1,15,000 for the 125cc engine or ₹1,35,000 for the 160cc engine, after applying the ₹50,000 early bird discount) is only due at the time of delivery. You can pay it directly to the authorized showroom/delivery center during physical vehicle handover. Local taxes like RTO registration and insurance are extra at that time."
              },
              {
                q: "What is included with the bike configuration?",
                a: "Your booking includes either the 125cc or 160cc hybrid motor (based on selection), a portable Lithium-Ion battery (72v 15A/H capacity), standard 5AMP home charger, and pre-integrated IoT smart tracking system. Early bird bookings also receive a free ₹12,000 gifts bundle (₹8,000 KGV Smart Helmet, ₹2,500 goodies, and ₹1,500 KGV Club Membership)."
              }
            ].map((faq, index) => {
              return (
                <details key={index} className="group border-b border-slate-800/80 pb-4 cursor-pointer outline-none">
                  <summary className="flex justify-between items-center font-semibold text-slate-200 hover:text-emerald-400 transition-colors list-none py-2 text-sm sm:text-base">
                    <span>{faq.q}</span>
                    <span className="text-emerald-400 font-bold transition-transform duration-300 group-open:rotate-45">+</span>
                  </summary>
                  <p className="text-slate-400 text-xs sm:text-sm mt-2 leading-relaxed pl-1">
                    {faq.a}
                  </p>
                </details>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
};